/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import { TDialogComponent } from "../../../dialogs/Dialog";
import { RELEASE_DIALOG_ACTION, ReleaseDialog } from "./ReleaseDialog";
import { DIALOG_SIZE, DialogPanel } from "../../../ui/components/Dialogs/DialogPanel";
import { DialogHeader } from "../../../ui/components/Dialogs/DialogHeader";
import { DialogContent } from "../../../ui/components/Dialogs/DialogContent";
import { DialogButtons } from "../../../ui/components/Dialogs/DialogButtons";
import { ACTION_BUTTON_SIZE, ACTION_BUTTON_STYLE, ActionButton } from "../../../ui/components/ActionButton";
import { errorListGroupHasFatalErrors } from "../../../errors/errorsUtils";
import { ReleaseDialogErrors } from "./components/ReleaseDialogErrors";
import { BEM, useTranslate } from "@adapptio/lib-components";
import { isNonEmptyArray } from "@adapptio/lib-shared";
import { useDeepCompareMemo } from "use-deep-compare";
import { useFieldState, useStateWithEvent } from "../../../ui/hooks";
import { ReleaseDialogSeparator } from "./components/DeployDialogSeparator";
import { ReleaseDialogUnsavedResources } from "./components/DeployDialogUnsavedResources";
import { FieldRow } from "../../../ui/components/Fields/FieldRow";
import { FieldGroup } from "../../../ui/components/Fields/FieldGroup";
import { FieldText } from "../../../ui/components/Fields/FieldText";
import { FieldNote } from "../../../ui/components/Fields/FieldNote";
import { ValidatorString } from "@adapptio/lib-blueprint";
import { Icon, ICON_SIZE } from "../../../ui/components/Icon";

export const ReleaseDialogComponent: TDialogComponent<ReleaseDialog> = ({ dialog }) => {
	const baseClass = BEM("edt-release-dialog");
	const t = useTranslate();

	const scmInfo = useStateWithEvent(dialog.onScmInfoChange, () => dialog.getScmInfo());
	const releaseList = useStateWithEvent(dialog.onReleaseListChange, () => dialog.getReleaseList());
	const statusInfo = useStateWithEvent(dialog.onStatusInfoChange, () => dialog.getStatusInfo());
	const errors = useStateWithEvent(dialog.onErrorsChanged, () => dialog.getErrors());
	const unsavedViewsInstances = useStateWithEvent(dialog.onUnsavedChangesChange, () =>
		dialog.getUnsavedViewsInstances()
	);

	const [ fieldState, setFieldState ] = useFieldState(
		{
			tagName: {
				defaultValue: releaseList.nextTagName || "",
				validators: [ ValidatorString({ required: true, min: 1 }) ]
			},
			releaseNotes: {
				defaultValue: ""
			}
		},
		[]
	);

	const latestTags = useMemo(() => {
		return releaseList.releases
			.sort((a, b) => a.tagName.localeCompare(b.tagName, undefined, { numeric: true }))
			.reverse()
			.slice(0, 7)
			.map(release => release.tagName).join(", ");
	}, [ releaseList.releases ]);

	const hasFatalErrors = useDeepCompareMemo(() => errorListGroupHasFatalErrors(errors), [ errors ]);

	const appNotSaved = isNonEmptyArray(unsavedViewsInstances);
	console.log('SCM INFO', scmInfo);
	const canRelease = ((scmInfo.pushMode === 'on-release' || scmInfo.pushMode === 'on-save') && scmInfo.hasRemote) || scmInfo.pushMode === 'never';
	const savingAndReleasing = statusInfo.currentAction === RELEASE_DIALOG_ACTION.SAVE_AND_RELEASE
	const Releasing = statusInfo.currentAction === RELEASE_DIALOG_ACTION.RELEASE;

	const actionsDisabled = savingAndReleasing || Releasing || !fieldState.tagName.value;

	const primaryButtonStyle: ACTION_BUTTON_STYLE = hasFatalErrors
		? ACTION_BUTTON_STYLE.DANGER
		: ACTION_BUTTON_STYLE.PRIMARY;

	const handleClose = useCallback(() => {
		dialog.close({ success: true });
	}, [ dialog ]);

	const handleRelease = useCallback(async () => {
		try {
			await dialog.release(fieldState.tagName.value, fieldState.releaseNotes.value);
			handleClose();
		} catch (err) {
			console.log(err);
		}
	}, [ fieldState, handleClose ]);

	const handleSaveAndRelease = useCallback(async () => {
		try {
			await dialog.saveAndRelease(fieldState.tagName.value, fieldState.releaseNotes.value);
			handleClose();
		} catch (err) {
			console.log(err);
		}
	}, [ fieldState, handleClose ]);

	return (
		<DialogPanel size={DIALOG_SIZE.MEDIUM} className={baseClass.toClassName()}>
			<DialogHeader title={t("editor", "Release")} icon="mdi/package-up" onClose={handleClose} />

			<DialogContent>
				<FieldRow className="edt-w-full">
					<FieldGroup>
						<FieldText
							value={fieldState.tagName.value}
							validationErrors={fieldState.tagName.validationErrors}
							placeholder="Enter tag name"
							onChange={setFieldState.tagName}
							className="w-full"
						/>
					</FieldGroup>
				</FieldRow>

				<FieldRow className={baseClass.element("latest-tags").add("edt-w-full").toClassName()}>
					<FieldNote>Latest tags: {latestTags}</FieldNote>
				</FieldRow>

				<FieldRow className="edt-w-full">
					<FieldGroup>
						<FieldText
							value={fieldState.releaseNotes.value}
							validationErrors={fieldState.releaseNotes.validationErrors}
							placeholder="Enter release notes"
							onChange={setFieldState.releaseNotes}
							className="w-full"
							multiLine
							maxRows={6}
						/>
					</FieldGroup>
				</FieldRow>

				{!canRelease && (
					<FieldRow className={baseClass.element("push-mode-notice").toClassName()}>
						<FieldNote color="warning">
							Warning: The configured Git push mode is "{scmInfo.pushMode}", but no remote repository is set. To make a release, please configure git remote or change push mode to "never".
						</FieldNote>
					</FieldRow>
				)}

				{scmInfo.pushMode === 'never' && (
					<FieldRow className={baseClass.element("push-mode-notice").toClassName()}>
						<FieldNote color="notice-hc">
							Push mode is set to "never". Release will be made as a local tag without pushing to remote repository.
						</FieldNote>
					</FieldRow>
				)}

				{scmInfo.pushMode !== 'never' && canRelease && (
					<>
						<FieldRow className={baseClass.element("push-mode-notice").add("edt-w-full").toClassName()}>
							<FieldNote>
								Release will create a git tag and push it to the remote repository:
							</FieldNote>
						</FieldRow>
						<FieldRow className={baseClass.element("remote-info").add("edt-w-full").toClassName()}>
							<FieldNote>
								<Icon name="mdi/source-branch" size={ICON_SIZE.XSMALL} />
								<span className={baseClass.element("remote-pre").toClassName()}>{scmInfo.remoteUrl}</span>
								<span className={baseClass.element("remote-pre").toClassName()}>({scmInfo.workingBranch})</span>
							</FieldNote>
						</FieldRow>
					</>
				)}

				<ReleaseDialogSeparator />

				<ReleaseDialogErrors errors={errors} />

				{appNotSaved && (
					<>
						<ReleaseDialogSeparator />

						<ReleaseDialogUnsavedResources viewsInstances={unsavedViewsInstances} />
					</>
				)}
			</DialogContent>

			<DialogButtons>
				<ActionButton
					label={t("editor", "Close")}
					onClick={handleClose}
					icon="mdi/close"
					style={ACTION_BUTTON_STYLE.DEFAULT}
					size={ACTION_BUTTON_SIZE.LARGE}
				/>
				{appNotSaved ? (
					<>
						<ActionButton
							label={
								Releasing ? t("editor", "Releasing...") : t("editor", "Release Without Saving")
							}
							onClick={handleRelease}
							icon="mdi/check"
							style={ACTION_BUTTON_STYLE.DEFAULT}
							size={ACTION_BUTTON_SIZE.LARGE}
							isLoading={Releasing}
							disabled={actionsDisabled}
						/>
						<ActionButton
							label={
								savingAndReleasing
									? t("editor", "Saving and Releasing...")
									: t("editor", "Save All & Release")
							}
							onClick={handleSaveAndRelease}
							icon="mdi/check"
							style={primaryButtonStyle}
							size={ACTION_BUTTON_SIZE.LARGE}
							isLoading={savingAndReleasing}
							disabled={actionsDisabled}
						/>
					</>
				) : (
					<ActionButton
						label={
							Releasing || savingAndReleasing
								? t("editor", "Releasing...")
								: t("editor", "Release")
						}
						onClick={handleRelease}
						icon="mdi/check"
						style={primaryButtonStyle}
						size={ACTION_BUTTON_SIZE.LARGE}
						isLoading={Releasing || savingAndReleasing}
						disabled={actionsDisabled}
					/>
				)}
			</DialogButtons>
		</DialogPanel>
	);
};
